/**
 * Turkish Provinces (81 İl)
 *
 * Ordered by plate code (traffic registration number).
 * Used for address validation, dropdown selectors, and province-level filtering.
 */

export interface TurkishProvince {
  code: string; // 2-digit plate code (01-81)
  name: string; // Province name
}

export const TR_PROVINCES: readonly TurkishProvince[] = [
  { code: '01', name: 'Adana' },
  { code: '02', name: 'Adıyaman' },
  { code: '03', name: 'Afyonkarahisar' },
  { code: '04', name: 'Ağrı' },
  { code: '05', name: 'Amasya' },
  { code: '06', name: 'Ankara' },
  { code: '07', name: 'Antalya' },
  { code: '08', name: 'Artvin' },
  { code: '09', name: 'Aydın' },
  { code: '10', name: 'Balıkesir' },
  { code: '11', name: 'Bilecik' },
  { code: '12', name: 'Bingöl' },
  { code: '13', name: 'Bitlis' },
  { code: '14', name: 'Bolu' },
  { code: '15', name: 'Burdur' },
  { code: '16', name: 'Bursa' },
  { code: '17', name: 'Çanakkale' },
  { code: '18', name: 'Çankırı' },
  { code: '19', name: 'Çorum' },
  { code: '20', name: 'Denizli' },
  { code: '21', name: 'Diyarbakır' },
  { code: '22', name: 'Edirne' },
  { code: '23', name: 'Elazığ' },
  { code: '24', name: 'Erzincan' },
  { code: '25', name: 'Erzurum' },
  { code: '26', name: 'Eskişehir' },
  { code: '27', name: 'Gaziantep' },
  { code: '28', name: 'Giresun' },
  { code: '29', name: 'Gümüşhane' },
  { code: '30', name: 'Hakkari' },
  { code: '31', name: 'Hatay' },
  { code: '32', name: 'Isparta' },
  { code: '33', name: 'Mersin' },
  { code: '34', name: 'İstanbul' },
  { code: '35', name: 'İzmir' },
  { code: '36', name: 'Kars' },
  { code: '37', name: 'Kastamonu' },
  { code: '38', name: 'Kayseri' },
  { code: '39', name: 'Kırklareli' },
  { code: '40', name: 'Kırşehir' },
  { code: '41', name: 'Kocaeli' },
  { code: '42', name: 'Konya' },
  { code: '43', name: 'Kütahya' },
  { code: '44', name: 'Malatya' },
  { code: '45', name: 'Manisa' },
  { code: '46', name: 'Kahramanmaraş' },
  { code: '47', name: 'Mardin' },
  { code: '48', name: 'Muğla' },
  { code: '49', name: 'Muş' },
  { code: '50', name: 'Nevşehir' },
  { code: '51', name: 'Niğde' },
  { code: '52', name: 'Ordu' },
  { code: '53', name: 'Rize' },
  { code: '54', name: 'Sakarya' },
  { code: '55', name: 'Samsun' },
  { code: '56', name: 'Siirt' },
  { code: '57', name: 'Sinop' },
  { code: '58', name: 'Sivas' },
  { code: '59', name: 'Tekirdağ' },
  { code: '60', name: 'Tokat' },
  { code: '61', name: 'Trabzon' },
  { code: '62', name: 'Tunceli' },
  { code: '63', name: 'Şanlıurfa' },
  { code: '64', name: 'Uşak' },
  { code: '65', name: 'Van' },
  { code: '66', name: 'Yozgat' },
  { code: '67', name: 'Zonguldak' },
  { code: '68', name: 'Aksaray' },
  { code: '69', name: 'Bayburt' },
  { code: '70', name: 'Karaman' },
  { code: '71', name: 'Kırıkkale' },
  { code: '72', name: 'Batman' },
  { code: '73', name: 'Şırnak' },
  { code: '74', name: 'Bartın' },
  { code: '75', name: 'Ardahan' },
  { code: '76', name: 'Iğdır' },
  { code: '77', name: 'Yalova' },
  { code: '78', name: 'Karabük' },
  { code: '79', name: 'Kilis' },
  { code: '80', name: 'Osmaniye' },
  { code: '81', name: 'Düzce' },
] as const;

/**
 * Validate a Turkish province name.
 * Case-insensitive, supports Turkish characters.
 */
export function isValidTurkishProvince(name: string): boolean {
  return TR_PROVINCES.some(
    (p) => p.name.toLocaleLowerCase('tr-TR') === name.toLocaleLowerCase('tr-TR')
  );
}

/**
 * Get province by plate code (e.g., '34' → İstanbul).
 */
export function getProvinceByCode(code: string): TurkishProvince | undefined {
  return TR_PROVINCES.find((p) => p.code === code);
}

/**
 * Get province options for dropdown selectors.
 * Returns { value, label } format compatible with form select components.
 */
export function getProvinceOptions(): { value: string; label: string }[] {
  return TR_PROVINCES.map((p) => ({ value: p.name, label: `${p.name} (${p.code})` }));
}
